/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */
import type {
  IssueTypePickerIssueType$data,
  IssueTypePickerIssueType$key,
} from './__generated__/IssueTypePickerIssueType.graphql'
import {useAnalytics} from '@github-ui/use-analytics'
import {
  graphql,
  readInlineData,
  type PreloadedQuery,
  useLazyLoadQuery,
  usePaginationFragment,
  usePreloadedQuery,
  useRelayEnvironment,
} from 'react-relay'
import type {IssueTypePickerQuery} from './__generated__/IssueTypePickerQuery.graphql'
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {IssueOpenedIcon} from '@primer/octicons-react'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {type ExtendedItemProps, ItemPicker, type SharedBulkActionsItemPickerProps} from '../components/ItemPicker'

import {ERRORS} from '../constants/errors'
import {HOTKEYS} from '../constants/hotkeys'
import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import {SharedPicker} from './SharedPicker'
import {useItemPickerErrorFallback} from './useItemPickerErrorFallback'
import type {IssueTypePickerPaginated$key} from './__generated__/IssueTypePickerPaginated.graphql'
import type {IssueTypeListPaginationQuery} from './__generated__/IssueTypeListPaginationQuery.graphql'
import {commitUpdateIssueIssueTypePickerMutation} from '../mutations/update-issue-issue-type-picker-mutation'
import type {updateIssueIssueTypeBulkByQueryMutation$data} from '../mutations/__generated__/updateIssueIssueTypeBulkByQueryMutation.graphql'
import type {updateIssueIssueTypeBulkMutation$data} from '../mutations/__generated__/updateIssueIssueTypeBulkMutation.graphql'
import {commitUpdateIssueIssueTypeBulkByQueryMutation} from '../mutations/update-issue-issue-type-bulk-by-query-mutation'
import {commitUpdateIssueIssueTypeBulkMutation} from '../mutations/update-issue-issue-type-bulk-mutation'
import {SharedEmptyPicker} from './SharedEmptyPicker'

export const UNSET_ID = 'UNSET'

export const IssueTypeFragment = graphql`
  fragment IssueTypePickerIssueType on IssueType @inline {
    id
    name
    isEnabled
  }
`

export const IssueTypePickerGraphqlQuery = graphql`
  query IssueTypePickerQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      ...IssueTypePickerPaginated
      isExcludedFromIssueTypes
    }
  }
`

export type IssueType = IssueTypePickerIssueType$data

type IssuePickerBaseProps = {
  issueId?: string
  activeIssueType: IssueType | null
  showUnset?: boolean
  readonly: boolean
  insidePortal?: boolean
  shortcutEnabled: boolean
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  onIssueUpdate?: () => void
  /**
   * Whether to render the issue type picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  shortcutHintVisible?: boolean
}

type IssueTypePickerProps = IssuePickerBaseProps & {
  issueTypeQueryRef: PreloadedQuery<IssueTypePickerQuery>
}

type LazyIssueTypePickerProps = IssuePickerBaseProps & {
  repo: string
  owner: string
}

type IssueTypePickerInternalProps = IssuePickerBaseProps & {
  issueTypeQueryRef: IssueTypePickerPaginated$key
  onSelectionChange: (issueType: IssueType[]) => void
}

export function IssueTypePicker({issueId, onIssueUpdate, ...rest}: IssueTypePickerProps) {
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (selectedIssueTypes: IssueType[]) => {
      commitUpdateIssueIssueTypePickerMutation({
        environment,
        input: {
          issueId: issueId || '',
          issueType: selectedIssueTypes && selectedIssueTypes?.length > 0 ? selectedIssueTypes[0]! : null,
        },
        onError: () => addToast({type: 'error', message: ERRORS.couldNotUpdateIssueType}),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, issueId, onIssueUpdate],
  )

  return <IssueTypePickerBase issueId={issueId} onSelectionChange={onSelectionChange} {...rest} />
}

export function LazyIssueTypePicker(props: LazyIssueTypePickerProps) {
  const [fetchKey, setFetchKey] = useState(0)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: 'Cannot edit issue types right now',
    anchorElement: props.anchorElement,
  })

  return (
    <PreloadedQueryBoundary onRetry={() => setFetchKey(fetchKey + 1)} fallback={createFallbackComponent}>
      <LazyIssueTypePickerInternal {...props} fetchKey={fetchKey} />
    </PreloadedQueryBoundary>
  )
}

function LazyIssueTypePickerInternal({
  repo,
  owner,
  fetchKey,
  onIssueUpdate,
  issueId,
  ...rest
}: LazyIssueTypePickerProps & {fetchKey: number}) {
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (selectedIssueTypes: IssueType[]) => {
      commitUpdateIssueIssueTypePickerMutation({
        environment,
        input: {
          issueId: issueId || '',
          issueType: selectedIssueTypes && selectedIssueTypes?.length > 0 ? selectedIssueTypes[0]! : null,
        },
        onError: () => addToast({type: 'error', message: ERRORS.couldNotUpdateIssueType}),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, issueId, onIssueUpdate],
  )

  const {repository} = useLazyLoadQuery<IssueTypePickerQuery>(
    IssueTypePickerGraphqlQuery,
    {
      repo,
      owner,
    },
    {fetchKey},
  )
  return repository ? (
    <IssueTypePickerInternalBase
      issueTypeQueryRef={repository}
      issueId={issueId}
      {...rest}
      onSelectionChange={onSelectionChange}
    />
  ) : null
}

export function IssueTypePickerBase({
  issueTypeQueryRef,
  ...rest
}: IssueTypePickerProps & {onSelectionChange: (issueType: IssueType[]) => void}) {
  const preloadedData = usePreloadedQuery<IssueTypePickerQuery>(IssueTypePickerGraphqlQuery, issueTypeQueryRef)
  return preloadedData.repository && !preloadedData.repository.isExcludedFromIssueTypes ? (
    <IssueTypePickerInternalBase issueTypeQueryRef={preloadedData.repository} {...rest} />
  ) : null
}

function IssueTypePickerInternalBase({
  issueTypeQueryRef,
  readonly,
  activeIssueType,
  showUnset = false,
  onSelectionChange,
  insidePortal,
  shortcutEnabled,
  anchorElement,
  nested,
  shortcutHintVisible = true,
}: IssueTypePickerInternalProps) {
  const [filter, setFilter] = useState('')

  const {data, loadNext, isLoadingNext, hasNext} = usePaginationFragment<
    IssueTypeListPaginationQuery,
    IssueTypePickerPaginated$key
  >(
    graphql`
      fragment IssueTypePickerPaginated on Repository
      @argumentDefinitions(cursor: {type: "String"}, count: {type: "Int", defaultValue: 10})
      @refetchable(queryName: "IssueTypeListPaginationQuery") {
        issueTypes(first: $count, after: $cursor) @connection(key: "Repository_issueTypes") {
          edges {
            node {
              ...IssueTypePickerIssueType
            }
          }
          totalCount
        }
      }
    `,
    issueTypeQueryRef,
  )

  const totalNumberOfIssueTypes = data.issueTypes?.totalCount || 0
  const fetchedIssueTypes = useMemo(
    () =>
      data.issueTypes?.edges?.flatMap(a =>
        // eslint-disable-next-line no-restricted-syntax
        a?.node ? [readInlineData<IssueTypePickerIssueType$key>(IssueTypeFragment, a.node)] : [],
      ) || [],
    [data],
  )

  useEffect(() => {
    if (hasNext && !isLoadingNext && fetchedIssueTypes.length < VALUES.issueTypesMaxPreloadCount) {
      loadNext(VALUES.issueTypesPageSize)
    }
  }, [loadNext, fetchedIssueTypes, totalNumberOfIssueTypes, hasNext, isLoadingNext])

  const items = useMemo(() => {
    const repositoryIssueTypes = fetchedIssueTypes.slice().sort((a, b) => a.name.localeCompare(b.name))
    if (showUnset) {
      repositoryIssueTypes.unshift({
        id: UNSET_ID,
        name: 'No issue type',
        isEnabled: true,
        ' $fragmentType': 'IssueTypePickerIssueType',
      })
    }
    if (!filter) return repositoryIssueTypes
    return repositoryIssueTypes.filter(l => l.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [fetchedIssueTypes, filter, showUnset])

  const filterItems = useCallback((value: string) => {
    setFilter(value)
  }, [])

  const getItemKey = useCallback((issueType: IssueType) => issueType.id, [])

  const convertToItemProps = useCallback((issueType: IssueType): ExtendedItemProps<IssueType> => {
    return {
      id: issueType.id,
      text: issueType.name,
      source: issueType,
      leadingVisual: () => <IssueOpenedIcon size={16} />,
    }
  }, [])

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(anchorProps)
      }

      if (activeIssueType) {
        return (
          <SharedPicker
            label={LABELS.noIssueTypes}
            sharedPickerMainValue={activeIssueType?.name}
            anchorProps={readonly ? undefined : anchorProps}
            ariaLabel={LABELS.selectIssueTypes}
            readonly={readonly}
            nested={nested}
          />
        )
      } else {
        return (
          <SharedEmptyPicker
            leadingIcon={IssueOpenedIcon}
            hotKey={
              !readonly && shortcutEnabled && shortcutHintVisible ? HOTKEYS.issueTypePicker.toUpperCase() : undefined
            }
            label={LABELS.noIssueTypes}
            anchorProps={readonly ? undefined : anchorProps}
            readonly={readonly}
            nested={nested}
          />
        )
      }
    },
    [activeIssueType, anchorElement, shortcutHintVisible, nested, readonly, shortcutEnabled],
  )

  return (
    <ItemPicker
      items={items}
      initialSelectedItems={activeIssueType ? [activeIssueType] : []}
      openHotkey={shortcutEnabled ? HOTKEYS.issueTypePicker : undefined}
      filterItems={filterItems}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={`Filter Issue Types ${
        fetchedIssueTypes.length >= VALUES.issueTypesMaxPreloadCount
          ? `(${VALUES.issueTypesMaxPreloadCount} issue types loaded)`
          : ''
      }`}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      renderAnchor={renderAnchor}
      insidePortal={insidePortal}
      height={'large'}
      width={'medium'}
      nested={nested}
      resultListAriaLabel={'Issue Type results'}
    />
  )
}

type BulkIssueIssueTypePickerProps = Omit<IssueTypePickerProps, 'issueId'> & SharedBulkActionsItemPickerProps

export function BulkIssueIssueTypePicker({
  onError,
  onCompleted,
  issuesToActOn,
  useQueryForAction,
  repositoryId,
  query,
  activeIssueType,
  ...rest
}: BulkIssueIssueTypePickerProps) {
  const environment = useRelayEnvironment()
  const {sendAnalyticsEvent} = useAnalytics()
  const onSelectionChange = useCallback(
    (selectedIssueType: IssueType[]) => {
      const issueTypeId = selectedIssueType.length === 1 ? selectedIssueType[0]?.id : null
      if (!issueTypeId || issueTypeId === activeIssueType?.id) return
      const mutationArgs = {
        environment,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      sendAnalyticsEvent('issues_index.bulk_action_issue_type', 'BULK_ISSUE_ISSUE_TYPE_PICKER', {
        initialIssueTypeId: activeIssueType?.id ?? '',
        issueTypeId: issueTypeId === 'UNSET' ? '' : issueTypeId ?? '',
        issueIds: [...issuesToActOn],
      })

      if (useQueryForAction && repositoryId && query) {
        commitUpdateIssueIssueTypeBulkByQueryMutation({
          ...mutationArgs,
          input: {
            repositoryId,
            query,
            issueTypeId,
          },
          optimisticUpdateIds: issuesToActOn,
          onCompleted: ({updateIssuesBulkByQuery}: updateIssueIssueTypeBulkByQueryMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitUpdateIssueIssueTypeBulkMutation({
          ...mutationArgs,
          input: {
            ids: [...issuesToActOn],
            issueTypeId,
          },
          onCompleted: ({updateIssuesBulk}: updateIssueIssueTypeBulkMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
        })
      }
    },
    [
      activeIssueType?.id,
      environment,
      issuesToActOn,
      onCompleted,
      onError,
      query,
      repositoryId,
      sendAnalyticsEvent,
      useQueryForAction,
    ],
  )
  return (
    <IssueTypePickerBase
      showUnset={true}
      activeIssueType={activeIssueType}
      onSelectionChange={onSelectionChange}
      {...rest}
    />
  )
}

try{ IssueTypePicker.displayName ||= 'IssueTypePicker' } catch {}
try{ LazyIssueTypePicker.displayName ||= 'LazyIssueTypePicker' } catch {}
try{ LazyIssueTypePickerInternal.displayName ||= 'LazyIssueTypePickerInternal' } catch {}
try{ IssueTypePickerBase.displayName ||= 'IssueTypePickerBase' } catch {}
try{ IssueTypePickerInternalBase.displayName ||= 'IssueTypePickerInternalBase' } catch {}
try{ BulkIssueIssueTypePicker.displayName ||= 'BulkIssueIssueTypePicker' } catch {}